import { useCallback, useEffect, useState } from 'react';
import {
  Alert, Button, Empty, Form, Input, Modal, Popconfirm, Space, Table,
  Tag, Tooltip, Typography, Upload, message
} from 'antd';
import {
  ReloadOutlined, PlusOutlined, DeleteOutlined, FileTextOutlined,
  DownloadOutlined, InboxOutlined
} from '@ant-design/icons';
import { accountAttachmentsApi } from './accountAuditApi';

const { Text } = Typography;
const { Dragger } = Upload;

// ─────────────────────────────────────────────────────────────────
// Glass-i — Accounting Attachments panel.
//
// Stage 6B.7D    — metadata-only registration.
// Stage 6B.7D-2  — DigitalOcean Spaces backed binary upload +
//                  short-lived signed-URL download. The bucket is
//                  PRIVATE; we never display object_key / bucket /
//                  storage_path. The upload widget validates type
//                  + size client-side as a first gate; the backend
//                  re-validates against the real bytes.
// ─────────────────────────────────────────────────────────────────

const ALLOWED_MIME = new Set([
  'application/pdf', 'image/jpeg', 'image/jpg', 'image/png'
]);
const ALLOWED_EXT  = new Set(['pdf', 'jpg', 'jpeg', 'png']);
const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;

function fmtBytes(n) {
  const b = Number(n) || 0;
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(2)} MB`;
}

function fmtTs(v) {
  if (!v) return <span className="erp-grid__muted">—</span>;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return v;
  const iso = d.toISOString();
  return `${iso.slice(0, 10)} ${iso.slice(11, 16)}`;
}

function clientPreflightFile(file) {
  if (!file) return 'Choose a file to upload.';
  if (!ALLOWED_MIME.has(String(file.type || '').toLowerCase())) {
    const ext = String(file.name || '').split('.').pop().toLowerCase();
    if (!ALLOWED_EXT.has(ext)) {
      return `Unsupported file type. Allowed: PDF, JPG, PNG.`;
    }
  }
  if (file.size > MAX_FILE_SIZE_BYTES) {
    return `File exceeds the 5 MB limit (size: ${fmtBytes(file.size)}).`;
  }
  return null;
}

export default function AttachmentsPanel({
  entityType, entityId, canUpload, canDelete
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [busySave, setBusySave] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [form] = Form.useForm();

  const fetchRows = useCallback(async () => {
    if (!entityType || !entityId) return;
    setLoading(true);
    try {
      const data = await accountAttachmentsApi.list({ entityType, entityId });
      setRows(Array.isArray(data?.rows) ? data.rows : []);
    } catch (e) {
      message.error(e?.response?.data?.error?.message || 'Failed to load attachments.');
    } finally {
      setLoading(false);
    }
  }, [entityType, entityId]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const openAdd = useCallback(() => {
    form.resetFields();
    setPendingFile(null);
    form.setFieldsValue({ description: '' });
    setAddOpen(true);
  }, [form]);

  const submitUpload = useCallback(async () => {
    let values;
    try { values = await form.validateFields(); }
    catch (_e) { return; }
    if (!pendingFile) {
      message.warning('Pick a file to upload first.');
      return;
    }
    const err = clientPreflightFile(pendingFile);
    if (err) { message.error(err); return; }
    setBusySave(true);
    try {
      await accountAttachmentsApi.upload({
        entityType, entityId,
        description: values.description ? String(values.description).trim() : null,
        file:        pendingFile
      });
      message.success('Attachment uploaded.');
      setAddOpen(false);
      setPendingFile(null);
      await fetchRows();
    } catch (e) {
      message.error(e?.response?.data?.error?.message || 'Failed to upload attachment.');
    } finally {
      setBusySave(false);
    }
  }, [form, pendingFile, entityType, entityId, fetchRows]);

  const onDelete = useCallback(async (record) => {
    try {
      await accountAttachmentsApi.remove(record.id);
      message.success('Attachment removed.');
      await fetchRows();
    } catch (e) {
      message.error(e?.response?.data?.error?.message || 'Failed to remove attachment.');
    }
  }, [fetchRows]);

  const onDownload = useCallback(async (record) => {
    try {
      const { downloadUrl } = await accountAttachmentsApi.getDownloadUrl(record.id);
      if (!downloadUrl) {
        message.error('Could not obtain download link.');
        return;
      }
      // Open the signed URL in a new tab. The browser will either
      // render the PDF/image inline or trigger a download depending
      // on the user's content-disposition behaviour.
      window.open(downloadUrl, '_blank', 'noopener,noreferrer');
    } catch (e) {
      message.error(e?.response?.data?.error?.message || 'Failed to fetch download link.');
    }
  }, []);

  const columns = [
    { title: 'File', dataIndex: 'fileName', key: 'fileName',
      render: (v, r) => (
        <Space size={6}>
          <FileTextOutlined style={{ color: '#94a3b8' }} />
          <span>{r.originalFileName || v}</span>
          {r.mimeType ? (
            <code style={{ fontSize: 11, color: '#94a3b8' }}>{r.mimeType}</code>
          ) : null}
          {r.uploadStatus === 'deleted' ? (
            <Tag style={{ marginLeft: 4, background: 'transparent', borderColor: '#fca5a5', color: '#991b1b' }}>deleted</Tag>
          ) : null}
        </Space>
      ) },
    { title: 'Size', dataIndex: 'fileSizeBytes', key: 'size',
      width: 110, align: 'right',
      render: (v) => fmtBytes(v) },
    { title: 'Description', dataIndex: 'description', key: 'description',
      ellipsis: true,
      render: (v) => v || <span className="erp-grid__muted">—</span> },
    { title: 'Uploaded By', dataIndex: 'uploaderName', key: 'uploaderName',
      width: 160,
      render: (v) => v || <span className="erp-grid__muted">—</span> },
    { title: 'Uploaded At', dataIndex: 'uploadedAt', key: 'uploadedAt',
      width: 150, render: fmtTs },
    { title: '', key: '__actions__', width: 80, align: 'right',
      render: (_v, r) => (
        <Space size={2}>
          <Tooltip title="View / Download">
            <Button size="small" type="text"
              icon={<DownloadOutlined />}
              disabled={r.uploadStatus !== 'uploaded' || !r.storageProvider}
              onClick={() => onDownload(r)} />
          </Tooltip>
          {canDelete ? (
            <Popconfirm
              title={`Remove "${r.originalFileName || r.fileName}"?`}
              description="The attachment is soft-deleted and hidden from this list."
              okText="Remove" okButtonProps={{ danger: true }} cancelText="Keep"
              onConfirm={() => onDelete(r)}
            >
              <Button size="small" type="text" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          ) : null}
        </Space>
      )
    }
  ];

  return (
    <div>
      <Alert
        type="info" showIcon style={{ marginBottom: 10 }}
        message="Attachments"
        description={
          <span style={{ fontSize: 12 }}>
            Upload supporting documents such as bills, payment proofs,
            cheque scans, or voucher documents. Allowed: PDF, JPG, PNG.
            Max 5&nbsp;MB. Download links are short-lived (5 minutes).
          </span>
        }
      />
      <div style={{ display: 'flex', justifyContent: 'space-between',
                    alignItems: 'center', marginBottom: 8 }}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {rows.length} {rows.length === 1 ? 'attachment' : 'attachments'} on this entity.
        </Text>
        <Space>
          <Button size="small" icon={<ReloadOutlined />}
            onClick={fetchRows} loading={loading}>
            Refresh
          </Button>
          {canUpload ? (
            <Button size="small" type="primary" icon={<PlusOutlined />}
              onClick={openAdd}>
              Upload Attachment
            </Button>
          ) : null}
        </Space>
      </div>
      <Table
        size="small"
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={rows}
        pagination={false}
        locale={{ emptyText: <Empty description="No attachments uploaded yet." /> }}
      />

      <Modal
        title="Upload Attachment"
        open={addOpen}
        onCancel={() => { setAddOpen(false); setPendingFile(null); }}
        onOk={submitUpload}
        okButtonProps={{ loading: busySave }}
        okText="Upload"
        centered
        destroyOnClose
        maskClosable={false}
        className="erp-accounts-modal"
        width={520}
      >
        <Form form={form} layout="vertical" className="erp-accounts-form">
          <Form.Item label="File" required>
            <Dragger
              multiple={false}
              maxCount={1}
              accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
              beforeUpload={(file) => {
                const err = clientPreflightFile(file);
                if (err) { message.error(err); return Upload.LIST_IGNORE; }
                setPendingFile(file);
                return false;  // prevent auto-upload — we POST manually
              }}
              onRemove={() => setPendingFile(null)}
              fileList={pendingFile ? [{
                uid: '-1', name: pendingFile.name, status: 'done',
                size: pendingFile.size
              }] : []}
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">Click or drag a file here</p>
              <p className="ant-upload-hint" style={{ fontSize: 12 }}>
                PDF / JPG / PNG, up to 5 MB.
              </p>
            </Dragger>
          </Form.Item>
          <Form.Item label="Description" name="description">
            <Input.TextArea rows={2} maxLength={2000}
              placeholder="Why is this document attached? (optional)" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
